import React from 'react';
import { Box } from '@cp/ds/src/components/box';
import { Stack } from '@cp/ds/src/components/stack';
import { Typography } from '@cp/ds/src/components/typography';
import { useTheme } from '@cp/ds/src/hooks';
import { mergeSx } from '@cp/ds/src/utils/sx/merge';
import { feedbackSign } from '@common/model/catalog/feedback';
import { MOBILE_BREAKPOINT_THRESHOLD } from '@cp/ds';
import { formatCatalogItemDate } from '../../../catalogItem/@x/feedback';
import { FeedbackViewProps, FeedbackViewSlots } from './types';
import { FeedbackViewAuthorName } from './authorName';

const defaultSlots: FeedbackViewSlots = {
  authorName: FeedbackViewAuthorName,
};

export const FeedbackView: React.FC<FeedbackViewProps> = ({
  data,
  feedbackCollapsedLength,
  feedbackCollapsedMaxLength,
  sx,
  actions,
  slots,
}) => {
  const { authorName: InnerAuthorName } = { ...defaultSlots, ...slots };
  const { authorName, review, rating, createdAt: _createdAt } = data;
  const theme = useTheme();
  const createdAt = formatCatalogItemDate(new Date(_createdAt), true);

  const getCollapsedDefaultState = React.useCallback(() => {
    return (
      Boolean(feedbackCollapsedLength) &&
      Boolean(feedbackCollapsedMaxLength) &&
      (review?.text?.length ? review.text.length > (feedbackCollapsedMaxLength as number) : false)
    );
  }, [review?.text?.length, feedbackCollapsedLength, feedbackCollapsedMaxLength]);

  const [isCollapsed, setCollapsed] = React.useState(getCollapsedDefaultState());

  React.useEffect(() => {
    setCollapsed(getCollapsedDefaultState());
  }, [getCollapsedDefaultState]);

  const reviewText = React.useMemo(() => {
    if (!isCollapsed || !review?.text || !feedbackCollapsedLength || !feedbackCollapsedMaxLength) {
      return review?.text;
    }

    const matchEnd = review.text.slice(feedbackCollapsedLength).match(/[\s\.\,]+/);
    const end = matchEnd ? Number(matchEnd.index) + feedbackCollapsedLength : null;

    if (!end || end > feedbackCollapsedMaxLength) {
      return review.text.slice(0, feedbackCollapsedLength).concat('... ');
    }

    return review.text.slice(0, end).concat('... ');
  }, [isCollapsed, review?.text, feedbackCollapsedLength, feedbackCollapsedMaxLength]);

  return (
    <Box
      sx={mergeSx(
        {
          padding: { xs: '0 12px 12px 0', [MOBILE_BREAKPOINT_THRESHOLD]: '12px 20px 20px 12px' },
        },
        sx,
      )}
    >
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <InnerAuthorName authorName={authorName} />
        <Stack alignItems="center" direction="row" spacing={1}>
          <Typography component="div" sx={{ color: theme.palette.gray, ml: 1 }} variant="caption">
            {createdAt}
          </Typography>
          {actions}
        </Stack>
      </Stack>

      <Typography component="p" sx={{ mb: 0, mt: 1, flexGrow: 1 }} variant="body">
        {feedbackSign({ rating })} {reviewText}
        {isCollapsed ? (
          <Box
            component="button"
            onClick={() => setCollapsed(false)}
            role="button"
            sx={{
              typography: 'body bold',
              backgroundColor: 'transparent',
              border: 'none',
              color: theme.palette['brand pink'],
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:active, &:focus': { outline: 'none' },
              px: 0,
            }}
          >
            показать еще
          </Box>
        ) : null}
      </Typography>
    </Box>
  );
};

export type { FeedbackViewSlots } from './types';

export { FeedbackViewAuthorName };
